import React from "react";
import {
  Pencil, Loader2, AlertTriangle, Users, Calendar, Clock,
  Banknote, FileDown, ReceiptText, ExternalLink,
} from "lucide-react";
import { cx, Pill, formatMoneyXOF, clampDate, parseProviderFromNotes } from "./helpers.js";
import { paymentMethodLabel, providerLabel } from "../../constants/payment.js";
import { getPrestationPdfUrl } from "../../api/prestations.api.js";

export default function PrestationCard({
  prestation,
  statusConfig,
  paymentModeConfig,
  openDetailId,
  setOpenDetailId,
  detailsById,
  loadingDetailId,
  changingStatusId,
  generatingQuoteId,
  onEdit,
  onPay,
  onQuickStatus,
  onGenerateQuote,
  onToggleDetails,
}) {
  const s = statusConfig[prestation.status] || statusConfig.DRAFT;
  const pm = paymentModeConfig[prestation.payment_mode] || paymentModeConfig.ONE_SHOT;
  const amountTTC = Number(prestation.amount_ttc || prestation.amount || 0);
  const amountHT = Number(prestation.amount_ht || 0);
  const paidTotal = Number(prestation.paid_total || 0);
  const dueTotal = Number(prestation.due_total || amountTTC - paidTotal);
  const isOverdue = prestation.is_overdue;
  const canPay = dueTotal > 0 && !["DRAFT", "CANCELLED", "PAID"].includes(prestation.status);

  return (
    <div
      className={cx(
        "group rounded-3xl border bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md",
        isOverdue ? "border-rose-300 ring-1 ring-rose-100" : "border-slate-200"
      )}
    >
      {/* En-tête */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="truncate text-base font-extrabold text-slate-900">{prestation.title}</h3>
            <Pill tone={s.tone}>{s.label}</Pill>
            {isOverdue && (
              <Pill tone="red">
                <AlertTriangle className="h-3 w-3" /> Retard
              </Pill>
            )}
            {prestation.invoice_number && (
              <Pill tone="blue">
                <ReceiptText className="h-3 w-3" /> {prestation.invoice_number}
              </Pill>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <span className="rounded-full bg-slate-50 px-2 py-1 ring-1 ring-slate-200">
              {prestation.code || "—"}
            </span>
            {prestation.customer_name && (
              <span className="inline-flex items-center gap-1">
                <Users className="h-3.5 w-3.5" /> {prestation.customer_name}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={() => onEdit(prestation)}
          className="rounded-2xl border border-slate-200 bg-white p-2 opacity-90 hover:bg-slate-50"
          title="Modifier"
        >
          <Pencil className="h-4 w-4 text-slate-700" />
        </button>
      </div>

      {/* Description */}
      {prestation.description ? (
        <p className="mt-3 line-clamp-2 text-sm text-slate-600">{prestation.description}</p>
      ) : (
        <p className="mt-3 text-sm text-slate-400 italic">Aucune description.</p>
      )}

      {/* Mode paiement */}
      <div className="mt-4 flex items-center gap-2">
        <pm.icon className="h-4 w-4 text-slate-500" />
        <span className="text-xs text-slate-600 font-medium">{pm.label}</span>
        {prestation.tva_pct != null && (
          <span className="text-xs text-slate-400">• TVA {prestation.tva_pct}%</span>
        )}
      </div>

      {/* Statut paiement (dropdown) */}
      <div className="mt-3">
        <label className="text-[11px] font-semibold uppercase text-slate-500">Statut paiement</label>
        <select
          value={prestation.status || "DRAFT"}
          onChange={(e) => onQuickStatus(prestation, e.target.value)}
          disabled={changingStatusId === prestation.id}
          className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-800 outline-none focus:border-slate-300 focus:ring-2 focus:ring-slate-100 disabled:opacity-60"
        >
          {Object.entries(statusConfig).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
      </div>

      {/* Grille HT / TVA / TTC */}
      <div className="mt-4 grid grid-cols-3 gap-2">
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-2 text-center">
          <div className="text-[10px] text-slate-500 uppercase">HT</div>
          <div className="text-xs font-bold text-slate-900">{formatMoneyXOF(amountHT)}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-2 text-center">
          <div className="text-[10px] text-slate-500 uppercase">TVA</div>
          <div className="text-xs font-bold text-slate-900">{formatMoneyXOF(Number(prestation.amount_tva || 0))}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-2 text-center">
          <div className="text-[10px] text-slate-500 uppercase">TTC</div>
          <div className="text-xs font-bold text-slate-900">{formatMoneyXOF(amountTTC)}</div>
        </div>
      </div>

      {/* Barre de progression paiement */}
      {(paidTotal > 0 || dueTotal > 0) && (
        <div className="mt-3">
          <div className="flex items-center justify-between text-xs">
            <span className="text-slate-500">
              Payé: <span className="font-semibold text-emerald-600">{formatMoneyXOF(paidTotal)}</span>
            </span>
            <span className="text-slate-500">
              Reste: <span className={cx("font-semibold", isOverdue ? "text-rose-600" : "text-slate-700")}>
                {formatMoneyXOF(dueTotal)}
              </span>
            </span>
          </div>
          {amountTTC > 0 && (
            <div className="mt-1.5 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
              <div
                className="h-full rounded-full bg-emerald-500 transition-all"
                style={{ width: `${Math.min(100, (paidTotal / amountTTC) * 100)}%` }}
              />
            </div>
          )}
        </div>
      )}

      {/* Dates */}
      <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
        {prestation.service_date && (
          <span className="inline-flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" /> Service: {clampDate(prestation.service_date)}
          </span>
        )}
        {prestation.due_date && (
          <span className={cx("inline-flex items-center gap-1", isOverdue && "text-rose-600")}>
            <Clock className="h-3.5 w-3.5" /> Échéance: {clampDate(prestation.due_date)}
          </span>
        )}
      </div>

      {/* Projet lié */}
      {prestation.project_title && (
        <div className="mt-3 text-xs text-slate-500">
          Projet: <span className="font-medium text-slate-700">{prestation.project_title}</span>
        </div>
      )}

      {/* Badge devis lié */}
      {(prestation.quote_id || prestation.quote_external_id) && (
        <div className="mt-3 flex items-center gap-2 rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700">
          <ReceiptText className="h-3.5 w-3.5 shrink-0" />
          <span>Devis lié :</span>
          <span className="font-semibold">{prestation.quote_external_id || "—"}</span>
          <a href="/quotes" className="ml-auto inline-flex items-center gap-1 font-medium hover:underline" title="Voir le devis">
            Voir <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      )}

      {/* Boutons principaux */}
      <div className="mt-5 flex gap-2">
        <button
          onClick={() => onEdit(prestation)}
          className="flex-1 rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 hover:bg-slate-50"
        >
          Modifier
        </button>
        {canPay && (
          <button
            onClick={() => onPay(prestation)}
            className="flex-1 rounded-2xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 inline-flex items-center justify-center gap-2"
          >
            <Banknote className="h-4 w-4" /> Payer
          </button>
        )}
      </div>

      {/* Actions document */}
      <div className="mt-2 flex gap-2">
        {prestation.status !== "CANCELLED" && prestation.status !== "DRAFT" && (
          <a
            href={getPrestationPdfUrl(prestation.id)}
            download
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 transition-colors"
            title="Télécharger la facture PDF"
          >
            <FileDown className="h-3.5 w-3.5" /> Facture PDF
          </a>
        )}
        {!prestation.quote_id && prestation.status !== "CANCELLED" && prestation.status !== "PAID" && (
          <button
            onClick={() => onGenerateQuote(prestation)}
            disabled={generatingQuoteId === prestation.id}
            className="flex-1 inline-flex items-center justify-center gap-2 rounded-2xl border border-violet-200 bg-violet-50 px-3 py-2 text-xs font-semibold text-violet-700 hover:bg-violet-100 transition-colors disabled:opacity-60"
            title="Générer un devis depuis cette prestation"
          >
            {generatingQuoteId === prestation.id
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <ReceiptText className="h-3.5 w-3.5" />
            }
            {generatingQuoteId === prestation.id ? "Génération…" : "Générer devis"}
          </button>
        )}
      </div>

      {/* Bouton historique */}
      <button
        onClick={() => onToggleDetails(prestation.id)}
        className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
      >
        {openDetailId === prestation.id ? "Masquer historique" : "Voir historique"}
      </button>

      {/* Historique paiements */}
      {openDetailId === prestation.id && (
        <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
          {loadingDetailId === prestation.id ? (
            <div className="text-xs text-slate-500">Chargement historique…</div>
          ) : (
            <>
              {Array.isArray(detailsById[prestation.id]?.payments) && detailsById[prestation.id].payments.length > 0 ? (
                <div className="space-y-2">
                  {detailsById[prestation.id].payments.map((p) => {
                    const method = String(p?.payment_method || p?.method || "").toLowerCase();
                    const { provider, cleanNotes } = parseProviderFromNotes(p?.notes || "");
                    const methodText =
                      method === "mobile" && provider
                        ? `${paymentMethodLabel("mobile_money")} (${providerLabel(provider)})`
                        : paymentMethodLabel(method);
                    return (
                      <div key={p.id} className="rounded-xl border border-slate-200 bg-white p-3 text-xs">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-slate-900">{formatMoneyXOF(p.amount)}</span>
                          <span className="text-slate-500">{clampDate(p.paid_at)}</span>
                        </div>
                        <div className="mt-1 text-slate-600">
                          Méthode: <span className="font-medium">{methodText || "—"}</span>
                        </div>
                        {p.reference && <div className="mt-1 text-slate-500">Réf: {p.reference}</div>}
                        {cleanNotes && <div className="mt-1 text-slate-500">Note: {cleanNotes}</div>}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-xs text-slate-500">Aucun paiement enregistré pour cette prestation.</div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
